import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Download, Loader2, Plus, DatabaseBackup, Clock } from 'lucide-react'
import { csrfHeaders } from '@/lib/csrf'

interface Backup {
  id: number
  status: 'pending' | 'in_progress' | 'completed' | 'failed'
  trigger: 'manual' | 'scheduled'
  size_bytes: number | null
  size_display: string
  created_at: string
  has_download: boolean
}

interface BackupsTabProps {
  projectId: number
}

const STATUS_BADGE_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'secondary',
  in_progress: 'default',
  completed: 'outline',
  failed: 'destructive',
}

const POLL_INTERVAL = 5_000

export function BackupsTab({ projectId }: BackupsTabProps) {
  const { t } = useTranslation(['projects', 'common'])
  const [backups, setBackups] = useState<Backup[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [downloading, setDownloading] = useState<number | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const waitingForBackupRef = useRef(false)
  const prevStatusesRef = useRef<Map<number, string>>(new Map())

  const fetchBackups = useCallback(async () => {
    try {
      const response = await fetch(`/projects/${projectId}/backups`, {
        headers: csrfHeaders(),
      })
      if (response.ok) {
        const data = await response.json()
        const newBackups: Backup[] = data.backups || []

        // Detect state transitions for toast notifications
        const prevStatuses = prevStatusesRef.current
        for (const backup of newBackups) {
          const prev = prevStatuses.get(backup.id)
          if (prev && (prev === 'pending' || prev === 'in_progress')) {
            if (backup.status === 'completed') {
              toast.success(t('projects:settings.backups.backupCompleted'))
            } else if (backup.status === 'failed') {
              toast.error(t('projects:settings.backups.backupFailed'))
            }
          }
        }

        // Update previous statuses
        const newStatuses = new Map<number, string>()
        for (const backup of newBackups) {
          newStatuses.set(backup.id, backup.status)
        }
        prevStatusesRef.current = newStatuses

        // If we were waiting for a backup to appear, check if it's there now
        if (waitingForBackupRef.current && newBackups.length > 0) {
          const hasNew = newBackups.some(
            (b) => b.status === 'pending' || b.status === 'in_progress'
          )
          if (hasNew) {
            waitingForBackupRef.current = false
          }
        }

        setBackups(newBackups)
      }
    } catch {
      // Silently handle fetch errors
    } finally {
      setLoading(false)
    }
  }, [projectId, t])

  useEffect(() => {
    fetchBackups()
  }, [fetchBackups])

  const hasActiveBackup = backups.some(
    (b) => b.status === 'pending' || b.status === 'in_progress'
  )

  const shouldPoll = hasActiveBackup || waitingForBackupRef.current

  // Auto-refresh while any backup is active or we're waiting for one to appear
  useEffect(() => {
    if (shouldPoll) {
      intervalRef.current = setInterval(fetchBackups, POLL_INTERVAL)
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [shouldPoll, fetchBackups])

  const startPolling = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    intervalRef.current = setInterval(fetchBackups, POLL_INTERVAL)
  }

  const handleCreateBackup = async () => {
    setCreating(true)
    try {
      const response = await fetch(`/projects/${projectId}/create_backup`, {
        method: 'POST',
        headers: csrfHeaders(),
      })

      if (response.ok) {
        toast.success(t('projects:settings.backups.backupStarted'))
        waitingForBackupRef.current = true
        startPolling()
      }
    } catch {
      // Silently handle errors
    } finally {
      setCreating(false)
    }
  }

  const handleDownload = async (backupId: number) => {
    setDownloading(backupId)
    try {
      const response = await fetch(
        `/projects/${projectId}/backups/${backupId}/download`,
        { headers: csrfHeaders() }
      )

      if (response.ok) {
        const data = await response.json()
        if (data.url) {
          const link = document.createElement('a')
          link.href = data.url
          link.download = ''
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      }
    } catch {
      // Silently handle errors
    } finally {
      setDownloading(null)
    }
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isDisabled = creating || hasActiveBackup || waitingForBackupRef.current

  return (
    <Card className="shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <DatabaseBackup className="h-5 w-5" />
              {t('projects:settings.backups.title')}
            </CardTitle>
            <CardDescription className="mt-1.5">
              {t('projects:settings.backups.description')}
              <span className="block mt-1 text-xs">
                {t('projects:settings.backups.retention')}
              </span>
            </CardDescription>
          </div>
          <Button
            onClick={handleCreateBackup}
            disabled={isDisabled}
            size="sm"
          >
            {(creating || waitingForBackupRef.current || hasActiveBackup) ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            {(creating || waitingForBackupRef.current)
              ? t('projects:settings.backups.creating')
              : hasActiveBackup
                ? t('projects:settings.backups.backupInProgress')
                : t('projects:settings.backups.createBackup')}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : backups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <DatabaseBackup className="h-10 w-10 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">
              {t('projects:settings.backups.noBackups')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">{t('projects:settings.backups.status')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">{t('projects:settings.backups.type')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">{t('projects:settings.backups.size')}</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">{t('projects:settings.backups.date')}</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">{t('projects:settings.backups.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {backups.map((backup) => (
                  <tr key={backup.id} className="border-b last:border-0">
                    <td className="px-3 py-2">
                      <Badge variant={STATUS_BADGE_VARIANTS[backup.status] || 'secondary'}>
                        {backup.status === 'in_progress' && (
                          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                        )}
                        {t(`projects:settings.backups.statuses.${backup.status}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        {backup.trigger === 'scheduled' && (
                          <Clock className="h-3.5 w-3.5" />
                        )}
                        {t(`projects:settings.backups.triggers.${backup.trigger}`)}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">
                      {backup.size_display || '—'}
                    </td>
                    <td className="px-3 py-2 text-sm text-muted-foreground">
                      {formatDate(backup.created_at)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {backup.has_download && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(backup.id)}
                          disabled={downloading === backup.id}
                        >
                          {downloading === backup.id ? (
                            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Download className="mr-1 h-3.5 w-3.5" />
                          )}
                          {t('projects:settings.backups.download')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
